import { ItemizedLineItem } from '@rta/rta-blueprint-library';
import { VerifyQuoteItem } from '@external';

const DEAL_STAGE_LABELS: Record<string, string> = {
	'appointmentscheduled': 'Appointment Scheduled',
	'qualifiedtobuy': 'Qualified to Buy',
	'presentationscheduled': 'Presentation Scheduled',
	'decisionmakerboughtin': 'Decision Maker Bought In',
	'contractsent': 'Contract Sent',
	'closedwon': 'Won - Deposit Received',
	'47761693': 'Won - Blueprints Pending Approval',
	'closedlost': 'Won - Blueprint Approved'
};

export const formatDealStageLabel = (dealStage: string | null | undefined): string => {
	if (!dealStage) return 'Unknown';
	return DEAL_STAGE_LABELS[dealStage] ?? dealStage;
};

type PromotionalItem = {
	sku: string;
	hsProductId?: string | null;
	adjustmentType?: string;
	priceAdjustment?: number;
	totalAdjustment?: number;
	hasDiscountRules?: boolean;
	isDiscount?: boolean;
};

type ItemizedMsrpData = {
	islands?: ItemizedLineItem[] | null;
	countertops?: ItemizedLineItem[];
	backsplashes?: ItemizedLineItem[];
	items?: ItemizedLineItem[];
	accessories?: ItemizedLineItem[];
};

/**
 * Build verification items array from itemized MSRP data.
 * hsProductId is included directly in the itemized data from the library.
 */
export const buildVerificationItems = (
	msrpData: ItemizedMsrpData,
	promotionalData?: PromotionalItem[] | null
): VerifyQuoteItem[] => {
	const items: VerifyQuoteItem[] = [];

	msrpData.islands?.forEach((item) => {
		items.push({
			sku: item.sku,
			hsProductId: item.hsProductId ?? null,
			quantity: item.qty,
			currentPrice: item.each,
			type: 'island',
			isFuelVariant: false
		});
	});

	msrpData.countertops
		?.filter((ct) => !ct.sku.startsWith('RTA-Chiseled'))
		.forEach((item) => {
			items.push({
				sku: item.sku,
				hsProductId: item.hsProductId ?? null,
				quantity: item.qty,
				currentPrice: item.each,
				type: 'countertop',
				isFuelVariant: false
			});
		});

	msrpData.backsplashes?.forEach((item) => {
		items.push({
			sku: item.sku,
			hsProductId: item.hsProductId ?? null,
			quantity: item.qty,
			currentPrice: item.each,
			type: 'backsplash',
			isFuelVariant: false
		});
	});

	// Cast works around tsc resolving @rta/rta-blueprint-library through node_modules to the main clone's copy of the package in this worktree, not this branch's edited source
	msrpData.items?.forEach((item: ItemizedLineItem & { isFuelVariant?: boolean }) => {
		items.push({
			sku: item.sku,
			hsProductId: item.hsProductId ?? null,
			quantity: item.qty,
			currentPrice: item.each,
			type: 'appliances',
			isFuelVariant: item.isFuelVariant ?? false
		});
	});

	msrpData.accessories?.forEach((item) => {
		items.push({
			sku: item.sku,
			hsProductId: item.hsProductId ?? null,
			quantity: item.qty,
			currentPrice: item.each,
			type: 'accessories',
			isFuelVariant: false
		});
	});

	promotionalData?.forEach((item) => {
		const price = item.hasDiscountRules ? item.totalAdjustment : (item.adjustmentType === 'FIXED' ? item.priceAdjustment : item.totalAdjustment);
		items.push({
			sku: item.sku,
			hsProductId: item.hsProductId ?? null,
			quantity: 1,
			currentPrice: Math.round(Math.abs(price ?? 0) * 100) / 100,
			type: item.isDiscount ? 'discount' : 'promotion',
			isFuelVariant: false
		});
	});

	return items;
};
